import { ChangeDetectorRef, Component, ElementRef, HostListener, OnInit, ViewChild } from "@angular/core";
import { DecimalPipe, NgTemplateOutlet } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { Observable } from "rxjs";
import Swal from "sweetalert2";

import { HomologacionModelosService } from "./homologacion-modelos.service";
import { PinAutorizadorService } from "../security/pin-autorizador/pin-autorizador.service";
import { PinAutorizadorRes, soloDigitos } from "../shared/model/security/pin-autorizador.model";
import { FirmaFrcComponent } from "../vacaciones/firma-frc/firma-frc.component";
import {
  coletilla,
  conSigno,
  dictamenClase,
  dictamenTexto,
  esHttps,
  fechaDmy,
  gateIcono,
  gateTexto,
  num,
  signo,
  tokensK,
  veces,
  veredictoClase,
  veredictoTexto,
} from "./homologacion-formato";
import { ComparativaComponent } from "./comparativa/comparativa.component";
import { FichaCatalogoComponent } from "./ficha-catalogo/ficha-catalogo.component";
import { MatrizTareaRubroComponent } from "./matriz-tarea-rubro/matriz-tarea-rubro.component";
import { RadarRubrosComponent, SerieRadar } from "./radar-rubros/radar-rubros.component";
import { TareaDetalleComponent } from "./tarea-detalle/tarea-detalle.component";
import {
  ACCION_TEXTO,
  DICTAMEN_TEXTO,
  DictamenHomologacion,
  DocHomologacion,
  DocTarea,
  EstadoGate,
  HomologacionEval,
  HomologacionFicha,
  HomologacionFichaPlan,
  HomologacionHistorial,
  HomologacionKpis,
  HomologacionLiga,
  HomologacionModelo,
  HomologacionPlan,
  HomologacionRes,
  SITUACION_TEXTO,
  SituacionModelo,
  VERSIONES_QUE_LEE,
  Veredicto,
} from "../shared/model/iso42001/homologacion-modelo.model";

type Pestana = "modelos" | "resultado" | "comparativa";
type Tono = "error" | "aviso" | "ok";

/** El motivo del reemplazo de una ficha: el mismo mínimo que exige el servidor (HomologacionFichaBuss.MOTIVO_MINIMO). */
const MOTIVO_FICHA_MINIMO = 10;

/** Los datos del modal de alta o edición de un modelo. */
interface EdicionModelo {
  idModelo: number | null;
  conEvaluaciones: boolean;
  activo: boolean;
  vigente: boolean;
  datos: {
    nombre: string;
    identificador: string;
    proveedor: string;
    usoPrevisto: string;
    idInventarioIa: number | null;
    idEvalProveedor: number | null;
  };
  original: string;
}

/** El modal de carga, en dos pasos: elegir archivo (previa) y confirmar (cargar). */
interface Carga {
  modelo: HomologacionModelo;
  archivo: File | null;
  plan: HomologacionPlan | null;
  motivo: string;
  revisando: boolean;
  cargando: boolean;
}

/** El modal de la ficha del catálogo: los mismos dos pasos que el de la evaluación (spec 5.8). */
interface CargaFicha {
  modelo: HomologacionModelo;
  archivo: File | null;
  plan: HomologacionFichaPlan | null;
  motivo: string;
  revisando: boolean;
  cargando: boolean;
}

/**
 * Las fichas del catálogo del modelo abierto en el Resultado, tenga o no
 * evaluaciones. Van aparte del Resultado: un modelo sin evaluaciones
 * también puede tener ficha.
 */
interface FichasDelModelo {
  idModelo: number;
  /** La vigente primero: solo encabezados (fichas.do). */
  fichas: HomologacionFicha[];
  /** La vigente, completa (ficha.do); null si no tiene. */
  ficha: HomologacionFicha | null;
}

/** El Resultado abierto. */
interface Resultado {
  evaluacion: HomologacionEval;
  documento: DocHomologacion | null;
  baseline: HomologacionEval | null;
  modelo: HomologacionModelo | null;
  evaluaciones: HomologacionEval[];
}

/** Una configuración del modelo abierto, con su última evaluación no reemplazada. */
interface ConfiguracionDelModelo {
  evaluacion: HomologacionEval;
  /** Es la configuración adoptada del vigente (spec 5.7). */
  vigente: boolean;
}

/** Lo que se va capturando del dictamen de la evaluación abierta. */
interface Captura {
  eleccion: DictamenHomologacion | null;
  restricciones: string;
  comentario: string;
}

/** El cuadro de la firma: el trazo lo lleva app-firma-frc; aquí, el PIN. */
interface Firma {
  idEval: number;
  pin: string;
  enviando: boolean;
  mensaje: string;
}

/** Reabrir un dictamen, eliminar una evaluación o eliminar una ficha del catálogo: los tres piden PIN. */
interface AccionPin {
  tipo: "reabrir" | "eliminar" | "eliminarFicha";
  /** La evaluación que se reabre o se elimina; null en eliminarFicha. */
  evaluacion: HomologacionEval | null;
  /** Solo en eliminarFicha: la ficha que se borra. */
  ficha: HomologacionFicha | null;
  modelo: string;
  motivo: string;
  pin: string;
  enviando: boolean;
}

/**
 * Homologación de Modelos IA · gestión.
 *
 * Tres cosas de fondo, del diseño (docs/superpowers/specs/2026-09-23-...):
 * - COS NO CALIFICA: todo número sale del archivo o del servidor.
 * - El archivo trae texto escrito por un modelo: se pinta SOLO con
 *   interpolación. Nada que inyecte HTML: ni binding de HTML crudo, ni
 *   rodeos del sanitizador, ni el `html:` o el `title:` de Swal, que se
 *   pintan como HTML (aquí van `titleText:` y `text:`).
 * - La carga va en dos pasos y el servidor decide: la pantalla muestra lo
 *   que dijo la vista previa y manda el mismo archivo con su huella.
 *
 * - La ficha del catálogo (spec 5.8) informa y no califica: se carga con los
 *   mismos dos pasos, se muestra aparte y la escribió un tercero (solo
 *   interpolación; ligas solo https://).
 *
 * La ruta, admin/homologacion-modelos, es también la llave con la que el
 * servidor autoriza (HomologacionBuss.MENU).
 */
@Component({
  selector: "app-homologacion-modelos",
  standalone: true,
  imports: [
    DecimalPipe,
    FormsModule,
    NgTemplateOutlet,
    RadarRubrosComponent,
    MatrizTareaRubroComponent,
    TareaDetalleComponent,
    FirmaFrcComponent,
    ComparativaComponent,
    FichaCatalogoComponent,
  ],
  templateUrl: "./homologacion-modelos.component.html",
  // Primero lo común (también lo usan la consulta y la comparativa), luego lo de gestión.
  styleUrls: ["./homologacion-comun.css", "./homologacion-modelos.component.css"],
})
export class HomologacionModelosComponent implements OnInit {
  readonly SITUACION_TEXTO = SITUACION_TEXTO;
  readonly DICTAMEN_TEXTO = DICTAMEN_TEXTO;
  readonly POR_PAGINA = 5;
  readonly MOTIVO_FICHA_MINIMO = MOTIVO_FICHA_MINIMO;
  /** Colores del radar: el del modelo es el acento del sistema; el del baseline, gris.
   *  Son tokens de homologacion-comun.css para que sigan al tema. */
  private readonly COLOR_MODELO = "var(--hom-serie-1)";
  private readonly COLOR_BASELINE = "var(--hom-suave)";

  pestana: Pestana = "modelos";

  /* ----------------------------- Modelos ----------------------------- */
  modelos: HomologacionModelo[] = [];
  kpis: HomologacionKpis = {};
  cargandoLista = false;
  errorLista = "";
  pagina = 1;
  totalPaginas = 1;
  modelosPagina: HomologacionModelo[] = [];
  paginas: number[] = [];

  /* ------------------------- Modal del modelo ------------------------- */
  edicion: EdicionModelo | null = null;
  guardandoModelo = false;
  retirando = false;
  mensajeModelo = "";
  tonoModelo: Tono = "error";
  historial: HomologacionHistorial[] = [];
  historialEstado = "";
  inventario: HomologacionLiga[] = [];
  evalProveedores: HomologacionLiga[] = [];
  private catalogosCargados = false;

  /* -------------------------- Modal de carga -------------------------- */
  carga: Carga | null = null;
  mensajeCarga = "";
  tonoCarga: Tono = "error";

  /* ------------------ Modal de la ficha del catálogo ------------------ */
  cargaFicha: CargaFicha | null = null;
  mensajeCargaFicha = "";
  tonoCargaFicha: Tono = "error";

  /* ----------------------------- Resultado ----------------------------- */
  resultado: Resultado | null = null;
  /** Las configuraciones del modelo abierto, cada una con su última evaluación. */
  configuracionesDelModelo: ConfiguracionDelModelo[] = [];
  /** Un modelo sin evaluaciones: el Resultado lo dice y ofrece cargar. */
  sinEvaluaciones: HomologacionModelo | null = null;
  cargandoResultado = false;
  errorResultado = "";
  descargando = false;
  /** Las fichas del catálogo del modelo abierto (spec 5.8). */
  fichasDel: FichasDelModelo | null = null;
  descargandoFicha = false;
  /** Cuenta las peticiones del Resultado: solo la última se pinta. */
  private pedidoResultado = 0;
  /** Cuenta las peticiones de las fichas del modelo abierto: solo la última se pinta. */
  private pedidoFichas = 0;
  nombresRubro: Record<string, string> = {};
  rubrosMatriz: { id: string; nombre: string }[] = [];
  etiquetasRadar: string[] = [];
  seriesRadar: SerieRadar[] = [];

  /* ------------------------------ Dictamen ------------------------------ */
  /**
   * Si mi perfil tiene la llave autorizar de Homologación (viene en el panel
   * de modelos): decide si se ven Firmar, Reabrir y Eliminar. No se toma del
   * puedeAutorizar del PIN, que dice si puedo definir PIN para CUALQUIER
   * módulo (Evaluaciones IA Solser también lo pide). Solo oculta botones: el
   * servidor vuelve a exigir la llave y el PIN en cada petición.
   */
  autoriza = false;
  /** Mi PIN: si lo tengo, si está bloqueado y cuántos intentos quedan. */
  pin: PinAutorizadorRes | null = null;
  captura: Captura = this.capturaNueva();
  firma: Firma | null = null;
  /** Lo que avisó el servidor al firmar (no bloquea): se queda a la vista. */
  avisosFirma: string[] = [];
  accionPin: AccionPin | null = null;
  mensajeAccion = "";

  @ViewChild(FirmaFrcComponent) private firmaFrc?: FirmaFrcComponent;

  /** A dónde regresa el foco al cerrar un cuadro. */
  private focoDesde: HTMLElement | null = null;

  constructor(
    private service: HomologacionModelosService,
    private pinService: PinAutorizadorService,
    private host: ElementRef<HTMLElement>,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.cargarModelos();
    this.cargarPin();
  }

  /* =============================== Modelos =============================== */

  cargarModelos(): void {
    this.cargandoLista = true;
    this.errorLista = "";
    this.service.modelos().subscribe({
      next: (res) => {
        this.cargandoLista = false;
        if (res.codigoError) {
          this.errorLista = this.mensaje(res, "No se pudieron consultar los modelos.");
          this.pinta();
          return;
        }
        this.modelos = res.modelos || [];
        this.kpis = res.kpis || {};
        this.autoriza = res.autoriza === true;
        this.paginar();
        // Firmar o reabrir cambian la situación y el vigente del modelo abierto;
        // y si el Resultado se abrió antes de tener la lista, aquí se completa.
        const r = this.resultado;
        const fresco = r ? this.modelos.find((x) => x.idModelo === r.evaluacion.idModelo) : null;
        if (r && fresco) {
          r.modelo = fresco;
          this.armaVista();
        }
        this.pinta();
      },
      error: () => {
        this.cargandoLista = false;
        this.errorLista = "No se pudieron consultar los modelos: sin conexión con el servidor.";
        this.pinta();
      },
    });
  }

  irAPagina(n: number): void {
    if (n < 1 || n > this.totalPaginas || n === this.pagina) {
      return;
    }
    this.pagina = n;
    this.paginar();
  }

  private paginar(): void {
    this.totalPaginas = Math.max(1, Math.ceil(this.modelos.length / this.POR_PAGINA));
    this.pagina = Math.min(Math.max(1, this.pagina), this.totalPaginas);
    const inicio = (this.pagina - 1) * this.POR_PAGINA;
    this.modelosPagina = this.modelos.slice(inicio, inicio + this.POR_PAGINA);
    const desde = Math.max(1, this.pagina - 2);
    const hasta = Math.min(this.totalPaginas, desde + 4);
    this.paginas = [];
    for (let n = Math.max(1, hasta - 4); n <= hasta; n++) {
      this.paginas.push(n);
    }
  }

  get rango(): string {
    if (!this.modelos.length) {
      return "";
    }
    const inicio = (this.pagina - 1) * this.POR_PAGINA + 1;
    const fin = Math.min(this.modelos.length, inicio + this.POR_PAGINA - 1);
    return `${inicio}–${fin} de ${this.modelos.length}`;
  }

  situacionClase(s?: SituacionModelo): string {
    switch (s) {
      case "VIGENTE":
      case "HOMOLOGADO":
        return "is-active";
      case "NO_HOMOLOGADO":
        return "is-danger";
      case "EVALUADO":
        return "is-pending";
      default:
        return "is-inactive";
    }
  }

  situacionTexto(s?: SituacionModelo): string {
    return s ? SITUACION_TEXTO[s] || s : "—";
  }

  veredictoTexto(v?: Veredicto | null): string {
    return veredictoTexto(v);
  }

  veredictoClase(v?: Veredicto | null): string {
    return veredictoClase(v);
  }

  /** La misma coletilla que Evaluaciones IA Solser: el veredicto no es el dictamen. */
  coletilla(e?: HomologacionEval | null): string {
    return e ? coletilla(e.estatus) : "";
  }

  accionTexto(a: string): string {
    return ACCION_TEXTO[a] || a;
  }

  /* ========================== Modal del modelo ========================== */

  registrar(): void {
    this.abrirEdicion(null);
  }

  editar(m: HomologacionModelo): void {
    this.abrirEdicion(m);
  }

  private abrirEdicion(m: HomologacionModelo | null): void {
    const datos = {
      nombre: m?.nombre || "",
      identificador: m?.identificador || "",
      proveedor: m?.proveedor || "",
      usoPrevisto: m?.usoPrevisto || "",
      idInventarioIa: m?.idInventarioIa ?? null,
      idEvalProveedor: m?.idEvalProveedor ?? null,
    };
    this.edicion = {
      idModelo: m?.idModelo ?? null,
      conEvaluaciones: !!m && (m.evaluaciones || 0) > 0,
      activo: m ? m.activo !== false : true,
      vigente: !!m?.vigente,
      datos,
      original: JSON.stringify(datos),
    };
    this.diceModelo("");
    this.historial = [];
    this.historialEstado = m ? "Cargando la bitácora…" : "";
    this.focoDesde = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.cargarCatalogos();
    if (m?.idModelo != null) {
      this.cargarHistorial(m.idModelo);
    }
    this.cdr.detectChanges();
    setTimeout(() => this.host.nativeElement.querySelector<HTMLElement>("#hom-modelo-nombre")?.focus());
  }

  private cargarCatalogos(): void {
    if (this.catalogosCargados) {
      return;
    }
    this.service.catalogos().subscribe({
      next: (res) => {
        if (res.codigoError) {
          this.diceModelo(this.mensaje(res, "No se pudieron consultar el inventario y las evaluaciones de proveedor."));
          this.pinta();
          return;
        }
        this.inventario = res.inventario || [];
        this.evalProveedores = res.evalProveedores || [];
        this.catalogosCargados = true;
        this.pinta();
      },
      error: () => {
        this.diceModelo("No se pudieron consultar el inventario y las evaluaciones de proveedor.");
        this.pinta();
      },
    });
  }

  private cargarHistorial(idModelo: number): void {
    this.service.historial(idModelo).subscribe({
      next: (res) => {
        if (this.edicion?.idModelo !== idModelo) {
          // Llegó tarde: el cuadro ya es de otro modelo, o se cerró.
          return;
        }
        if (res.codigoError) {
          this.historialEstado = this.mensaje(res, "No se pudo consultar la bitácora.");
          this.pinta();
          return;
        }
        this.historial = res.historial || [];
        this.historialEstado = this.historial.length ? "" : "Sin movimientos todavía.";
        this.pinta();
      },
      error: () => {
        if (this.edicion?.idModelo === idModelo) {
          this.historialEstado = "No se pudo consultar la bitácora.";
          this.pinta();
        }
      },
    });
  }

  get edicionSinCambios(): boolean {
    return !!this.edicion && JSON.stringify(this.edicion.datos) === this.edicion.original;
  }

  get edicionIncompleta(): boolean {
    return !this.edicion || !this.edicion.datos.nombre.trim() || !this.edicion.datos.identificador.trim();
  }

  textoLiga(l: HomologacionLiga): string {
    return (l.folio ? l.folio + " · " : "") + l.nombre + (l.estatus && l.estatus !== "ACTIVO" && l.estatus !== "FIRMADA"
      && l.estatus !== "ABIERTA" ? ` (${l.estatus.toLowerCase()})` : "");
  }

  /** La evaluación de un proveedor con su dictamen a la vista, como la lee Evaluaciones IA Solser. */
  textoEvalProveedor(l: HomologacionLiga): string {
    if (l.estatus === "FIRMADA") {
      return `${l.nombre} · ${l.dictamen === "APROBADO" ? "Aprobado" : "No aprobado"} · firmada`;
    }
    return `${l.nombre} · sin firmar`;
  }

  guardarModelo(): void {
    const e = this.edicion;
    if (!e || this.guardandoModelo || this.edicionIncompleta || this.edicionSinCambios) {
      return;
    }
    this.guardandoModelo = true;
    this.diceModelo("");
    this.service
      .guardarModelo({
        idModelo: e.idModelo ?? undefined,
        nombre: e.datos.nombre.trim(),
        identificador: e.datos.identificador.trim(),
        proveedor: e.datos.proveedor.trim() || null,
        usoPrevisto: e.datos.usoPrevisto.trim() || null,
        idInventarioIa: e.datos.idInventarioIa,
        idEvalProveedor: e.datos.idEvalProveedor,
      })
      .subscribe({
        next: (res) => {
          this.guardandoModelo = false;
          if (res.codigoError) {
            this.diceModelo(this.mensaje(res, "No se pudo guardar el modelo."));
            this.pinta();
            return;
          }
          const nuevo = e.idModelo == null;
          e.idModelo = res.idGenerado ?? e.idModelo;
          e.original = JSON.stringify(e.datos);
          this.diceModelo(nuevo ? "Modelo registrado; quedó en la bitácora." : "Guardado; quedó en la bitácora.", "ok");
          if (e.idModelo != null) {
            this.cargarHistorial(e.idModelo);
          }
          this.cargarModelos();
          this.pinta();
        },
        error: () => {
          this.guardandoModelo = false;
          this.diceModelo("No se pudo guardar el modelo: sin conexión con el servidor.");
          this.pinta();
        },
      });
  }

  /** Retira el modelo (activo = 0). No se borra nada, y el vigente no se retira. */
  retirar(): void {
    const e = this.edicion;
    if (!e || e.idModelo == null || !e.activo || e.vigente || this.retirando || this.hayCuadroSwal()) {
      return;
    }
    const idModelo = e.idModelo;
    // titleText y text, nunca title ni html: Swal pinta esos dos como HTML.
    Swal.fire({
      titleText: "¿Retirar el modelo?",
      text: `${e.datos.nombre} deja de recibir evaluaciones. No se borra nada: su historial se queda.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Retirar",
      cancelButtonText: "Cancelar",
      // En linea le gana a la variable de swal que ponen los temas; en el
      // clasico --cos-acento no existe y queda el vino de siempre.
      confirmButtonColor: "var(--cos-acento, #b3261e)",
    }).then((x) => {
      if (!x.isConfirmed || this.retirando) {
        return;
      }
      this.retirando = true;
      this.pinta();
      this.service.bajaModelo(idModelo).subscribe({
        next: (res) => {
          this.retirando = false;
          // Lo que diga el servidor es de ESE modelo: si el cuadro ya es de otro, solo se refresca la lista.
          const mismo = this.edicion === e;
          if (res.codigoError) {
            if (mismo) {
              this.diceModelo(this.mensaje(res, "No se pudo retirar el modelo."));
            }
            this.pinta();
            return;
          }
          if (mismo) {
            e.activo = false;
            this.diceModelo("Modelo retirado; quedó en la bitácora.", "ok");
            this.cargarHistorial(idModelo);
          }
          this.cargarModelos();
          this.pinta();
        },
        error: () => {
          this.retirando = false;
          if (this.edicion === e) {
            this.diceModelo("No se pudo retirar el modelo: sin conexión con el servidor.");
          }
          this.pinta();
        },
      });
    });
  }

  /**
   * @param suave Escape, el fondo o el ✕: con cambios sin guardar no cierra,
   *   avisa. Cancelar cierra siempre (como el modal de Editar de Evaluaciones).
   */
  cerrarEdicion(suave = false): void {
    if (this.guardandoModelo) {
      return;
    }
    if (suave && this.edicion && !this.edicionSinCambios) {
      this.diceModelo("Hay cambios sin guardar: pulsa Guardar o Cancelar.", "aviso");
      return;
    }
    this.edicion = null;
    this.historial = [];
    this.devuelveFoco();
  }

  private diceModelo(texto: string, tono: Tono = "error"): void {
    this.mensajeModelo = texto;
    this.tonoModelo = tono;
  }

  /* =========================== Modal de carga =========================== */

  abrirCarga(m: HomologacionModelo): void {
    if (m.activo === false) {
      return;
    }
    this.carga = { modelo: m, archivo: null, plan: null, motivo: "", revisando: false, cargando: false };
    this.diceCarga("");
    this.focoDesde = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.cdr.detectChanges();
    setTimeout(() => this.host.nativeElement.querySelector<HTMLElement>("#hom-carga-archivo")?.focus());
  }

  /** Paso 1: elegir el archivo manda la vista previa. No se escribe nada. */
  alElegirArchivo(evento: Event): void {
    const input = evento.target as HTMLInputElement;
    const archivo = input.files && input.files.length ? input.files[0] : null;
    const c = this.carga;
    if (!c || !archivo || c.cargando) {
      return;
    }
    c.archivo = archivo;
    c.plan = null;
    c.revisando = true;
    this.diceCarga("");
    this.service.previa(archivo, c.modelo.idModelo as number).subscribe({
      next: (res) => {
        // Otro cuadro u otro archivo: esta respuesta ya no es de lo que se ve.
        if (this.carga !== c || c.archivo !== archivo) {
          return;
        }
        c.revisando = false;
        if (res.codigoError) {
          c.archivo = null;
          this.limpiaInputArchivo();
          this.diceCarga(this.mensaje(res, "No se pudo revisar el archivo."));
          this.pinta();
          return;
        }
        c.plan = res.plan || null;
        if (!c.plan?.puedeCargar) {
          // Para que el mismo archivo, ya corregido, se pueda volver a elegir:
          // el navegador no avisa si se elige otra vez el que ya estaba.
          this.limpiaInputArchivo();
        }
        this.pinta();
      },
      error: () => {
        if (this.carga === c && c.archivo === archivo) {
          c.revisando = false;
          c.archivo = null;
          this.limpiaInputArchivo();
          this.diceCarga("No se pudo revisar el archivo: sin conexión con el servidor.");
          this.pinta();
        }
      },
    });
  }

  get puedeCargar(): boolean {
    const c = this.carga;
    return !!c && !!c.archivo && !!c.plan && c.plan.puedeCargar && !c.revisando && !c.cargando
      && (!c.plan.pideMotivo || !!c.motivo.trim());
  }

  /** Paso 2: el MISMO archivo, con la huella de lo que se revisó. */
  confirmarCarga(): void {
    const c = this.carga;
    if (!c || !this.puedeCargar || !c.archivo || !c.plan?.huella) {
      return;
    }
    c.cargando = true;
    this.diceCarga("");
    this.service.cargar(c.archivo, c.modelo.idModelo as number, c.plan.huella, c.plan.pideMotivo ? c.motivo : undefined,
      c.plan.idEvalReemplaza ?? null)
      .subscribe({
        next: (res) => {
          c.cargando = false;
          if (res.codigoError) {
            if (res.codigoError === "VAL-HUELLA") {
              // Vuelve al paso 1: lo revisado ya no es lo que se iba a cargar.
              c.archivo = null;
              c.plan = null;
              this.limpiaInputArchivo();
            }
            this.diceCarga(this.mensaje(res, "No se pudo cargar la evaluación."));
            this.pinta();
            return;
          }
          const modelo = c.modelo;
          this.carga = null;
          this.devuelveFoco();
          this.aviso("Evaluación cargada; quedó en la bitácora del modelo.", "success");
          this.cargarModelos();
          if (res.idGenerado != null) {
            this.abrirResultado(res.idGenerado, modelo);
          }
          this.pinta();
        },
        error: () => {
          c.cargando = false;
          this.diceCarga("No se pudo cargar la evaluación: sin conexión con el servidor. No se guardó nada.");
          this.pinta();
        },
      });
  }

  cerrarCarga(suave = false): void {
    const c = this.carga;
    if (!c || c.cargando) {
      return;
    }
    if (suave && c.motivo.trim()) {
      this.diceCarga("Escribiste un motivo: pulsa Cancelar para salir sin cargar.", "aviso");
      return;
    }
    this.carga = null;
    this.devuelveFoco();
  }

  private limpiaInputArchivo(): void {
    const input = this.host.nativeElement.querySelector<HTMLInputElement>("#hom-carga-archivo");
    if (input) {
      input.value = "";
    }
  }

  private diceCarga(texto: string, tono: Tono = "error"): void {
    this.mensajeCarga = texto;
    this.tonoCarga = tono;
  }

  kb(bytes?: number | null): string {
    if (bytes == null) {
      return "—";
    }
    return bytes < 1024 * 1024 ? `${(bytes / 1024).toFixed(1)} KB` : `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  }

  /* =================== Modal de la ficha del catálogo =================== */
  /*
   * Los mismos dos pasos que «Cargar evaluación» (spec 5.8): elegir el
   * archivo manda la vista previa (no escribe), y «Cargar» manda el MISMO
   * archivo con su huella, el motivo y la ficha que reemplaza, si reemplaza.
   */

  abrirCargaFicha(m: HomologacionModelo): void {
    if (m.activo === false) {
      return;
    }
    this.cargaFicha = { modelo: m, archivo: null, plan: null, motivo: "", revisando: false, cargando: false };
    this.diceCargaFicha("");
    this.focoDesde = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.cdr.detectChanges();
    setTimeout(() => this.host.nativeElement.querySelector<HTMLElement>("#hom-ficha-archivo")?.focus());
  }

  /** Paso 1: elegir el archivo manda la vista previa. No se escribe nada. */
  alElegirFicha(evento: Event): void {
    const input = evento.target as HTMLInputElement;
    const archivo = input.files && input.files.length ? input.files[0] : null;
    const c = this.cargaFicha;
    if (!c || !archivo || c.cargando) {
      return;
    }
    c.archivo = archivo;
    c.plan = null;
    c.revisando = true;
    this.diceCargaFicha("");
    this.service.fichaPrevia(archivo, c.modelo.idModelo as number).subscribe({
      next: (res) => {
        // Otro cuadro u otro archivo: esta respuesta ya no es de lo que se ve.
        if (this.cargaFicha !== c || c.archivo !== archivo) {
          return;
        }
        c.revisando = false;
        if (res.codigoError) {
          c.archivo = null;
          this.limpiaInputFicha();
          this.diceCargaFicha(this.mensaje(res, "No se pudo revisar la ficha."));
          this.pinta();
          return;
        }
        c.plan = res.fichaPlan || null;
        if (!c.plan?.puedeCargar) {
          // Para que la misma ficha, ya corregida, se pueda volver a elegir.
          this.limpiaInputFicha();
        }
        this.pinta();
      },
      error: () => {
        if (this.cargaFicha === c && c.archivo === archivo) {
          c.revisando = false;
          c.archivo = null;
          this.limpiaInputFicha();
          this.diceCargaFicha("No se pudo revisar la ficha: sin conexión con el servidor.");
          this.pinta();
        }
      },
    });
  }

  get puedeCargarFicha(): boolean {
    const c = this.cargaFicha;
    return !!c && !!c.archivo && !!c.plan && c.plan.puedeCargar && !c.revisando && !c.cargando
      && (!c.plan.pideMotivo || c.motivo.trim().length >= MOTIVO_FICHA_MINIMO);
  }

  /** Paso 2: la MISMA ficha, con la huella de lo que se revisó. */
  confirmarCargaFicha(): void {
    const c = this.cargaFicha;
    if (!c || !this.puedeCargarFicha || !c.archivo || !c.plan?.huella) {
      return;
    }
    c.cargando = true;
    this.diceCargaFicha("");
    this.service.fichaCargar(c.archivo, c.modelo.idModelo as number, c.plan.huella,
      c.plan.pideMotivo ? c.motivo : undefined, c.plan.idFichaReemplaza ?? null)
      .subscribe({
        next: (res) => {
          c.cargando = false;
          if (res.codigoError) {
            if (res.codigoError === "VAL-HUELLA") {
              // Vuelve al paso 1: lo revisado ya no es lo que se iba a cargar.
              c.archivo = null;
              c.plan = null;
              this.limpiaInputFicha();
            }
            this.diceCargaFicha(this.mensaje(res, "No se pudo cargar la ficha."));
            this.pinta();
            return;
          }
          const idModelo = c.modelo.idModelo as number;
          this.cargaFicha = null;
          this.devuelveFoco();
          this.aviso("Ficha cargada; quedó en la bitácora del modelo.", "success");
          this.cargarModelos();
          if (this.fichasDel?.idModelo === idModelo) {
            this.cargarFichas(idModelo);
          }
          this.pinta();
        },
        error: () => {
          c.cargando = false;
          this.diceCargaFicha("No se pudo cargar la ficha: sin conexión con el servidor. No se guardó nada.");
          this.pinta();
        },
      });
  }

  cerrarCargaFicha(suave = false): void {
    const c = this.cargaFicha;
    if (!c || c.cargando) {
      return;
    }
    if (suave && c.motivo.trim()) {
      this.diceCargaFicha("Escribiste un motivo: pulsa Cancelar para salir sin cargar.", "aviso");
      return;
    }
    this.cargaFicha = null;
    this.devuelveFoco();
  }

  private limpiaInputFicha(): void {
    const input = this.host.nativeElement.querySelector<HTMLInputElement>("#hom-ficha-archivo");
    if (input) {
      input.value = "";
    }
  }

  private diceCargaFicha(texto: string, tono: Tono = "error"): void {
    this.mensajeCargaFicha = texto;
    this.tonoCargaFicha = tono;
  }

  /* ============================== Resultado ============================== */

  verResultado(m: HomologacionModelo): void {
    const ultima = m.ultimaEvaluacion;
    if (!ultima) {
      this.resultado = null;
      this.sinEvaluaciones = m;
      this.errorResultado = "";
      this.pestana = "resultado";
      if (m.idModelo != null) {
        // Sin evaluaciones también puede tener ficha del catálogo.
        this.cargarFichas(m.idModelo);
      }
      this.subeAlInicio();
      return;
    }
    this.abrirResultado(ultima.idEval, m);
  }

  abrirResultado(idEval: number, modelo?: HomologacionModelo | null): void {
    // La última que se pidió es la que se ve: si antes llega otra, se ignora.
    const pedido = ++this.pedidoResultado;
    this.cargandoResultado = true;
    this.errorResultado = "";
    this.sinEvaluaciones = null;
    this.pestana = "resultado";
    this.subeAlInicio();
    this.service.detalle(idEval).subscribe({
      next: (res) => {
        if (pedido !== this.pedidoResultado) {
          return;
        }
        this.cargandoResultado = false;
        if (res.codigoError || !res.evaluacion) {
          this.errorResultado = this.mensaje(res, "No se pudo abrir el resultado.");
          this.pinta();
          return;
        }
        const ev = res.evaluacion;
        const m = modelo || this.modelos.find((x) => x.idModelo === ev.idModelo) || null;
        if (this.resultado?.evaluacion.idEval !== ev.idEval) {
          // Lo capturado, los avisos y los cuadros eran de la otra evaluación.
          this.captura = this.capturaNueva();
          this.avisosFirma = [];
          this.firma = null;
          this.accionPin = null;
        }
        this.resultado = {
          evaluacion: ev,
          documento: res.documento || null,
          baseline: res.baseline || null,
          modelo: m,
          evaluaciones: [],
        };
        this.armaVista();
        this.cargarEvaluaciones(ev.idModelo);
        this.cargarFichas(ev.idModelo);
        this.pinta();
      },
      error: () => {
        if (pedido !== this.pedidoResultado) {
          return;
        }
        this.cargandoResultado = false;
        this.errorResultado = "No se pudo abrir el resultado: sin conexión con el servidor.";
        this.pinta();
      },
    });
  }

  private cargarEvaluaciones(idModelo: number): void {
    const r = this.resultado;
    this.service.evaluaciones(idModelo).subscribe({
      next: (res) => {
        if (this.resultado === r && r && !res.codigoError) {
          r.evaluaciones = res.evaluaciones || [];
          this.pinta();
        }
      },
    });
  }

  /**
   * Las fichas del catálogo del modelo (la vigente primero) y, si hay
   * vigente, la ficha completa. Otro modelo empieza en blanco; el mismo se
   * refresca sin parpadear. Si algo falla, el Resultado se ve igual, sin el
   * bloque.
   *
   * ficha.do se pide SIEMPRE, aunque la vigente sea la misma: los días desde
   * «verificado» y los precios del harness los calcula el servidor en cada
   * consulta, y cambian al cargar, reemplazar o eliminar una ficha o una
   * evaluación. Mientras llega, se queda la que se ve (sin parpadeo), salvo
   * que la vigente ya sea otra.
   */
  private cargarFichas(idModelo: number): void {
    if (this.fichasDel?.idModelo !== idModelo) {
      this.fichasDel = { idModelo, fichas: [], ficha: null };
    }
    const d = this.fichasDel;
    const pedido = ++this.pedidoFichas;
    this.service.fichas(idModelo).subscribe({
      next: (res) => {
        if (this.fichasDel !== d || pedido !== this.pedidoFichas || res.codigoError) {
          return;
        }
        d.fichas = res.fichas || [];
        const vigente = d.fichas.find((f) => f.estatus === "VIGENTE");
        if (!vigente || vigente.idFicha == null) {
          d.ficha = null;
          this.pinta();
          return;
        }
        if (d.ficha?.idFicha !== vigente.idFicha) {
          d.ficha = null;
        }
        this.service.ficha(vigente.idFicha).subscribe({
          next: (x) => {
            if (this.fichasDel === d && pedido === this.pedidoFichas && !x.codigoError) {
              // Un objeto nuevo: el bloque (OnPush) vuelve a armar sus precios y su aviso.
              d.ficha = x.ficha || null;
              this.pinta();
            }
          },
        });
        this.pinta();
      },
    });
  }

  /** La ficha que reemplazó a esta, si sigue en la lista: dice quién, cuándo y con qué motivo. */
  reemplazoDe(fichas: HomologacionFicha[], f: HomologacionFicha): HomologacionFicha | null {
    return f.idFicha == null ? null : fichas.find((x) => x.idFichaReemplazada === f.idFicha) || null;
  }

  /** Las fichas del modelo que está a la vista en el Resultado (con o sin evaluaciones). */
  get fichasAbiertas(): FichasDelModelo | null {
    const idModelo = this.resultado?.evaluacion.idModelo ?? this.sinEvaluaciones?.idModelo;
    return this.fichasDel && this.fichasDel.idModelo === idModelo ? this.fichasDel : null;
  }

  get legible(): boolean {
    const v = this.resultado?.documento?.version_formato;
    return v != null && VERSIONES_QUE_LEE.includes(v);
  }

  get razones(): string[] {
    const r = this.resultado?.documento?.resultado?.razones;
    return Array.isArray(r) ? r.filter((x) => typeof x === "string") : [];
  }

  get tareasDoc(): DocTarea[] {
    const t = this.resultado?.documento?.tareas;
    return this.legible && Array.isArray(t) ? t : [];
  }

  /**
   * Lo que reciben el radar, la matriz y el detalle de las tareas, armado
   * UNA vez por Resultado. Como getters darían un arreglo nuevo en cada
   * repintado, y el radar se rehace entero cuando cambia su entrada: con
   * cada tecla o cada trazo de la firma.
   */
  private armaVista(): void {
    const r = this.resultado;
    const rubros = r?.evaluacion.rubros || [];
    const nombres: Record<string, string> = {};
    for (const x of rubros) {
      nombres[x.rubro] = x.nombre;
    }
    this.nombresRubro = nombres;
    this.rubrosMatriz = rubros.map((x) => ({ id: x.rubro, nombre: x.nombre }));
    this.etiquetasRadar = rubros.map((x) => x.nombre);
    const series: SerieRadar[] = r ? [{
      nombre: r.modelo?.nombre || r.evaluacion.modelo || "Modelo",
      valores: rubros.map((x) => this.num(x.valor)),
      color: this.COLOR_MODELO,
    }] : [];
    if (r && !r.evaluacion.esBaseline && rubros.some((x) => this.num(x.valorBaseline) !== null)) {
      series.push({
        nombre: r.baseline?.modelo ? `${r.baseline.modelo} (baseline)` : "Baseline",
        valores: rubros.map((x) => this.num(x.valorBaseline)),
        color: this.COLOR_BASELINE,
      });
    }
    this.seriesRadar = series;
    this.configuracionesDelModelo = this.configuracionesDe(r);
  }

  /**
   * Por candidato_id, la evaluación más reciente que no está reemplazada:
   * vienen de la más reciente a la más vieja. Vigente es la configuración
   * de la evaluación adoptada; sin ella, ninguna se marca.
   */
  private configuracionesDe(r: Resultado | null): ConfiguracionDelModelo[] {
    if (!r) {
      return [];
    }
    const adoptada = r.evaluaciones.find((x) => x.idEval === r.modelo?.idEvalVigente);
    const vistas = new Set<string>();
    const lista: ConfiguracionDelModelo[] = [];
    for (const x of r.evaluaciones) {
      const clave = x.candidatoId || "";
      if (x.estatus === "REEMPLAZADA" || vistas.has(clave)) {
        continue;
      }
      vistas.add(clave);
      lista.push({
        evaluacion: x,
        vigente: !!r.modelo?.vigente && !!adoptada && adoptada.candidatoId === x.candidatoId,
      });
    }
    return lista;
  }

  /** Su baseline se recalificó después: los deltas son contra una calificación que ya no vale y no se firma. */
  get baselineReemplazado(): boolean {
    const r = this.resultado;
    return !!r && !r.evaluacion.esBaseline && r.evaluacion.idEvalBaseline != null
      && (!r.baseline || r.baseline.estatus === "REEMPLAZADA");
  }

  /** El delta tal como lo calculó el harness, con su signo. COS no lo recalcula. */
  conSigno(v?: number | null, decimales = 1): string {
    return conSigno(v, decimales);
  }

  signo(v?: number | null): "sube" | "baja" | "igual" {
    return signo(v);
  }

  /** Relativo del harness: 0.78× el del baseline. Menos es mejor en costo y latencia. */
  veces(v?: number | null): string {
    return veces(v);
  }

  tokensK(v?: number | null): string {
    return tokensK(v);
  }

  gateTexto(e: EstadoGate): string {
    return gateTexto(e);
  }

  gateIcono(e: EstadoGate): string {
    return gateIcono(e);
  }

  dictamenTexto(d?: DictamenHomologacion | null): string {
    return dictamenTexto(d);
  }

  fechaDmy(v?: string | null): string {
    return fechaDmy(v);
  }

  /** Una liga de la ficha del catálogo, solo si es https:// (spec 5.8); si no, se pinta como texto. */
  esHttps(v?: string | null): boolean {
    return esHttps(v);
  }

  /** Bajar el archivo tal cual: Blob con tipo fijo, como adjunto, nunca inline. */
  descargar(): void {
    const r = this.resultado;
    if (!r || this.descargando) {
      return;
    }
    this.descargando = true;
    this.service.descarga(r.evaluacion.idEval).subscribe({
      next: (res) => {
        this.descargando = false;
        this.pinta();
        if (res.codigoError || res.documentoTexto == null) {
          this.aviso(this.mensaje(res, "No se pudo bajar el archivo."), "error");
          return;
        }
        this.bajaJson(res.documentoTexto, r.evaluacion.nombreArchivo || `homologacion_${r.evaluacion.runId}`, "homologacion");
      },
      error: () => {
        this.descargando = false;
        this.pinta();
        this.aviso("No se pudo bajar el archivo: sin conexión con el servidor.", "error");
      },
    });
  }

  /** Bajar una ficha del catálogo tal cual la cargaron: igual que el archivo de la evaluación. */
  descargarFicha(f: HomologacionFicha): void {
    const idFicha = f.idFicha;
    if (idFicha == null || this.descargandoFicha) {
      return;
    }
    this.descargandoFicha = true;
    this.service.fichaDescarga(idFicha).subscribe({
      next: (res) => {
        this.descargandoFicha = false;
        this.pinta();
        if (res.codigoError || res.documentoTexto == null) {
          this.aviso(this.mensaje(res, "No se pudo bajar la ficha."), "error");
          return;
        }
        this.bajaJson(res.documentoTexto, res.ficha?.nombreArchivo || f.nombreArchivo || `ficha_${idFicha}`, "ficha");
      },
      error: () => {
        this.descargandoFicha = false;
        this.pinta();
        this.aviso("No se pudo bajar la ficha: sin conexión con el servidor.", "error");
      },
    });
  }

  /** Blob con tipo fijo, como adjunto, nunca inline. */
  private bajaJson(texto: string, nombre: string, porOmision: string): void {
    const blob = new Blob([texto], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    // Siempre .json: un archivo subido como «x.html» no se baja para abrirse como página.
    const base = nombre.replace(/\.[^./\\]*$/, "");
    a.download = `${base || porOmision}.json`;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  cargarDesdeResultado(): void {
    const m = this.resultado?.modelo || this.sinEvaluaciones;
    if (m) {
      this.abrirCarga(m);
    }
  }

  cargarFichaDesdeResultado(): void {
    const m = this.resultado?.modelo || this.sinEvaluaciones;
    if (m) {
      this.abrirCargaFicha(m);
    }
  }

  volverAModelos(): void {
    this.pestana = "modelos";
    this.subeAlInicio();
  }

  /* ============================== Dictamen ============================== */

  private cargarPin(): void {
    this.pinService.estado().subscribe({
      next: (res) => {
        // Si no se pudo saber, sin botones: el servidor exige la llave igual.
        this.pin = res.codigoError ? null : res;
        this.pinta();
      },
      error: () => {
        this.pin = null;
        this.pinta();
      },
    });
  }

  /** Lo vuelve a preguntar: por si lo definió en Ajustes, en otra pestaña. */
  revisarPin(): void {
    this.cargarPin();
  }

  get puedeAutorizar(): boolean {
    return this.autoriza;
  }

  get sinPin(): boolean {
    return !!this.pin && !this.pin.tienePin;
  }

  get pinBloqueado(): string | null {
    return this.pin?.tienePin && this.pin.bloqueadoHasta ? this.pin.bloqueadoHasta : null;
  }

  /** Los intentos que quedan, cuando ya se equivocó alguna vez (el servidor cuenta: 5 bloquean 15 minutos). */
  get avisoIntentos(): string {
    const n = this.pin?.intentosRestantes;
    if (n == null || n >= 5) {
      return "Con cinco fallos se bloquea 15 minutos.";
    }
    return `Te ${n === 1 ? "queda 1 intento" : `quedan ${n} intentos`} antes de que se bloquee 15 minutos.`;
  }

  private capturaNueva(): Captura {
    return { eleccion: null, restricciones: "", comentario: "" };
  }

  /** El modelo vigente de hoy, para decir a quién reemplaza un Adoptar. */
  get vigenteActual(): HomologacionModelo | null {
    return this.modelos.find((m) => m.vigente) || null;
  }

  /** Un modelo retirado no se adopta (el servidor lo rechaza igual). */
  get adoptable(): boolean {
    return this.resultado?.modelo?.activo !== false;
  }

  get textoAdoptar(): string {
    const m = this.resultado?.modelo;
    if (!this.adoptable) {
      return "El modelo está retirado: no se adopta.";
    }
    if (m?.vigente) {
      // Se adopta una configuración (spec 5.7): si es otra, pasa a ser la adoptada.
      const adoptada = this.configuracionesDelModelo.find((c) => c.vigente)?.evaluacion;
      const e = this.resultado?.evaluacion;
      if (adoptada && e && adoptada.candidatoId !== e.candidatoId) {
        return `Sigue como el modelo vigente, y esta configuración pasa a ser la adoptada en lugar de «${
          adoptada.nombreConfiguracion || adoptada.candidatoId}».`;
      }
      return "Sigue como el modelo vigente para desarrollo, con esta configuración.";
    }
    const antes = this.vigenteActual;
    return "Queda como el modelo vigente para desarrollo" + (antes ? `; ${antes.nombre} pasa a Homologado.` : ".");
  }

  elegirDictamen(d: DictamenHomologacion): void {
    if (d === "ADOPTAR" && !this.adoptable) {
      return;
    }
    this.captura.eleccion = d;
  }

  get capturaLista(): boolean {
    const c = this.captura;
    return !!c.eleccion && (c.eleccion !== "ADOPTAR_CON_RESTRICCIONES" || !!c.restricciones.trim());
  }

  /** La que reemplazó a la abierta, si está en la lista del modelo. */
  get reemplazante(): HomologacionEval | null {
    const r = this.resultado;
    return r ? r.evaluaciones.find((x) => x.idEvalReemplazada === r.evaluacion.idEval) || null : null;
  }

  dictamenClase(d?: DictamenHomologacion | null): string {
    return dictamenClase(d);
  }

  /** Qué se sella: lo lee quien firma en el cuadro de la firma. */
  get leyendaFirma(): string {
    const r = this.resultado;
    const d = this.captura.eleccion;
    if (!r || !d) {
      return "";
    }
    const efecto = d === "ADOPTAR" && !r.modelo?.vigente ? " El modelo queda como el vigente para desarrollo." : "";
    return `Tu firma y tu PIN sellan el dictamen «${DICTAMEN_TEXTO[d]}» de la evaluación #${r.evaluacion.idEval}.`
      + `${efecto} La evaluación queda congelada: reabrirla pide motivo y PIN.`;
  }

  /** El botón de app-firma-frc, además del trazo: un PIN completo y sin enviar. */
  get firmaLista(): boolean {
    const f = this.firma;
    return !!f && !f.enviando && !this.sinPin && !this.pinBloqueado && /^\d{6,8}$/.test(f.pin);
  }

  abrirFirma(): void {
    const r = this.resultado;
    if (!r || !this.puedeAutorizar || r.evaluacion.estatus !== "CARGADA" || !this.capturaLista
      || this.baselineReemplazado || this.firma || this.accionPin) {
      return;
    }
    this.firma = { idEval: r.evaluacion.idEval, pin: "", enviando: false, mensaje: "" };
    this.focoDesde = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.cdr.detectChanges();
    // El foco entra al cuadro: detrás del velo no se debe poder seguir con el teclado.
    setTimeout(() => this.enfocaEnFirma());
  }

  /** El PIN si está; si no, lo primero que se puede enfocar en el cuadro de la firma. */
  private enfocaEnFirma(): void {
    const caja = this.host.nativeElement.querySelector<HTMLElement>("app-firma-frc .ff__caja");
    const pin = caja?.querySelector<HTMLElement>("#hom-firma-pin");
    (pin || caja?.querySelector<HTMLElement>("a[href], button:not([disabled])"))?.focus();
  }

  /** app-firma-frc emitió el PNG del trazo: se manda con el dictamen y el PIN. */
  alFirmar(png: string): void {
    const f = this.firma;
    const r = this.resultado;
    const d = this.captura.eleccion;
    // Solo la evaluación que está a la vista: el cuadro no firma otra.
    if (!f || !r || !d || f.enviando || !this.firmaLista || f.idEval !== r.evaluacion.idEval) {
      return;
    }
    f.enviando = true;
    f.mensaje = "";
    this.service
      .firmar({
        idEval: f.idEval,
        dictamen: d,
        restricciones: d === "ADOPTAR_CON_RESTRICCIONES" ? this.captura.restricciones.trim() : null,
        comentario: this.captura.comentario.trim() || null,
        firma: png,
        pin: f.pin,
      })
      .subscribe({
        next: (res) => {
          f.enviando = false;
          f.pin = "";
          if (res.codigoError || !res.evaluacion) {
            f.mensaje = this.mensaje(res, "No se pudo firmar el dictamen. No se guardó nada.");
            this.trasFallaDePin(res);
            this.pinta();
            // El campo se deshabilitó mientras se mandaba: el foco vuelve a él.
            setTimeout(() => this.enfocaEnFirma());
            return;
          }
          this.firma = null;
          if (this.resultado === r) {
            r.evaluacion = res.evaluacion;
            this.armaVista();
            this.captura = this.capturaNueva();
            this.avisosFirma = res.avisos || [];
            this.cargarEvaluaciones(r.evaluacion.idModelo);
          }
          // El botón de Firmar ya no existe: el foco va al título del dictamen.
          this.devuelveFoco("#hom-dictamen-titulo");
          this.aviso("Dictamen firmado; quedó en la bitácora del modelo.", "success");
          this.cargarModelos();
          // Un PIN correcto limpia los intentos en el servidor.
          this.cargarPin();
          this.pinta();
        },
        error: () => {
          f.enviando = false;
          f.pin = "";
          f.mensaje = "No se pudo firmar el dictamen: sin conexión con el servidor. No se guardó nada.";
          this.pinta();
          setTimeout(() => this.enfocaEnFirma());
        },
      });
  }

  cerrarFirma(): void {
    if (!this.firma || this.firma.enviando) {
      return;
    }
    this.firma = null;
    this.devuelveFoco();
  }

  /**
   * Un PIN equivocado, bloqueado o sin definir, o un perfil sin la llave:
   * se vuelve a preguntar el estado para que el cuadro diga lo que sigue.
   */
  private trasFallaDePin(res: HomologacionRes): void {
    if (res.codigoError && (res.codigoError.startsWith("VAL-PIN") || res.codigoError === "VAL-PERFIL")) {
      this.cargarPin();
    }
  }

  abrirReabrir(): void {
    this.abrirAccion("reabrir");
  }

  abrirEliminar(): void {
    this.abrirAccion("eliminar");
  }

  /**
   * Eliminar una ficha del catálogo cargada por error: llave autorizar y PIN,
   * como eliminar una evaluación. Si era la vigente, la anterior no revive.
   */
  abrirEliminarFicha(f: HomologacionFicha): void {
    const d = this.fichasAbiertas;
    // Un cuadro a la vez: nunca uno encima del de la firma.
    if (!d || f.idFicha == null || !this.puedeAutorizar || this.accionPin || this.firma) {
      return;
    }
    const m = this.resultado?.modelo || this.sinEvaluaciones;
    this.accionPin = {
      tipo: "eliminarFicha",
      evaluacion: null,
      ficha: f,
      modelo: m?.nombre || this.resultado?.evaluacion.modelo || f.nombre || "Modelo",
      motivo: "",
      pin: "",
      enviando: false,
    };
    this.mensajeAccion = "";
    this.focoDesde = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.cdr.detectChanges();
    setTimeout(() => this.host.nativeElement.querySelector<HTMLElement>("#hom-accion-pin")?.focus());
  }

  private abrirAccion(tipo: "reabrir" | "eliminar"): void {
    const r = this.resultado;
    const esperado = tipo === "reabrir" ? "FIRMADA" : "CARGADA";
    // Un cuadro a la vez: nunca uno encima del de la firma.
    if (!r || !this.puedeAutorizar || r.evaluacion.estatus !== esperado || this.accionPin || this.firma) {
      return;
    }
    this.accionPin = {
      tipo,
      evaluacion: r.evaluacion,
      ficha: null,
      modelo: r.modelo?.nombre || r.evaluacion.modelo || "Modelo",
      motivo: "",
      pin: "",
      enviando: false,
    };
    this.mensajeAccion = "";
    this.focoDesde = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.cdr.detectChanges();
    const primero = tipo === "reabrir" ? "#hom-accion-motivo" : "#hom-accion-pin";
    setTimeout(() => this.host.nativeElement.querySelector<HTMLElement>(primero)?.focus());
  }

  get accionLista(): boolean {
    const a = this.accionPin;
    return !!a && !a.enviando && !this.sinPin && !this.pinBloqueado && /^\d{6,8}$/.test(a.pin)
      && (a.tipo !== "reabrir" || !!a.motivo.trim());
  }

  confirmarAccion(): void {
    const a = this.accionPin;
    if (!a || !this.accionLista) {
      return;
    }
    let peticion: Observable<HomologacionRes> | null = null;
    if (a.tipo === "eliminarFicha") {
      peticion = a.ficha?.idFicha != null ? this.service.fichaEliminar(a.ficha.idFicha, a.pin) : null;
    } else if (a.evaluacion) {
      peticion = a.tipo === "reabrir"
        ? this.service.reabrir(a.evaluacion.idEval, a.motivo.trim(), a.pin)
        : this.service.eliminar(a.evaluacion.idEval, a.pin);
    }
    if (!peticion) {
      return;
    }
    a.enviando = true;
    this.mensajeAccion = "";
    const r = this.resultado;
    const d = this.fichasDel;
    peticion.subscribe({
      next: (res) => {
        a.enviando = false;
        a.pin = "";
        if (res.codigoError) {
          this.mensajeAccion = this.mensaje(res, a.tipo === "reabrir"
            ? "No se pudo reabrir el dictamen. No se guardó nada."
            : a.tipo === "eliminarFicha"
            ? "No se pudo eliminar la ficha. No se borró nada."
            : "No se pudo eliminar la evaluación. No se borró nada.");
          this.trasFallaDePin(res);
          this.pinta();
          setTimeout(() => this.host.nativeElement.querySelector<HTMLElement>("#hom-accion-pin")?.focus());
          return;
        }
        this.accionPin = null;
        if (a.tipo === "reabrir") {
          if (r && this.resultado === r && res.evaluacion) {
            r.evaluacion = res.evaluacion;
            this.armaVista();
            this.captura = this.capturaNueva();
            this.avisosFirma = [];
            this.cargarEvaluaciones(r.evaluacion.idModelo);
          }
          // Primero el estado nuevo y luego el foco: el botón de Reabrir ya no existe.
          this.devuelveFoco("#hom-dictamen-titulo");
          this.aviso("Dictamen reabierto; quedó en la bitácora del modelo.", "success");
        } else if (a.tipo === "eliminarFicha") {
          // El modelo puede quedar sin ficha: la anterior no revive. Se quita de
          // una vez (su botón de Eliminar ya no existe) y luego se refresca.
          if (d && this.fichasDel === d) {
            const borrada = a.ficha?.idFicha;
            d.fichas = d.fichas.filter((x) => x.idFicha !== borrada);
            if (d.ficha?.idFicha === borrada) {
              d.ficha = null;
            }
            this.cargarFichas(d.idModelo);
          }
          this.devuelveFoco(".hom-pestanas__item.is-activa");
          this.aviso("Ficha eliminada; quedó en la bitácora del modelo.", "success");
        } else {
          // Ya no existe: de vuelta a la lista, que dice cuál es ahora la última.
          this.resultado = null;
          this.firma = null;
          this.pestana = "modelos";
          this.focoDesde = null;
          this.subeAlInicio();
          this.aviso("Evaluación eliminada; quedó en la bitácora del modelo.", "success");
        }
        // Un PIN correcto limpia los intentos en el servidor.
        this.cargarPin();
        this.cargarModelos();
        this.pinta();
      },
      error: () => {
        a.enviando = false;
        a.pin = "";
        this.mensajeAccion = "Sin conexión con el servidor. No se guardó nada.";
        this.pinta();
        setTimeout(() => this.host.nativeElement.querySelector<HTMLElement>("#hom-accion-pin")?.focus());
      },
    });
  }

  /** @param suave Escape, el fondo o el ✕: con un motivo escrito no cierra, avisa. */
  cerrarAccion(suave = false): void {
    const a = this.accionPin;
    if (!a || a.enviando) {
      return;
    }
    if (suave && a.motivo.trim()) {
      this.mensajeAccion = "Escribiste un motivo: pulsa Cancelar para salir sin reabrir.";
      return;
    }
    this.accionPin = null;
    this.devuelveFoco();
  }

  /**
   * El campo del PIN: solo dígitos, hasta 8. Se corrige el propio campo,
   * no solo el modelo: si no, una letra se quedaría a la vista.
   */
  digitos(evento: Event): string {
    const input = evento.target as HTMLInputElement;
    const limpio = soloDigitos(input.value);
    if (input.value !== limpio) {
      input.value = limpio;
    }
    return limpio;
  }

  /** Enter en el campo del PIN hace lo del botón de su cuadro. */
  enterEnPin(de: Firma | AccionPin): void {
    if (de === this.firma) {
      this.firmaFrc?.confirmar();
    } else if (de === this.accionPin) {
      this.confirmarAccion();
    }
  }

  irAResultado(): void {
    if (this.resultado || this.sinEvaluaciones) {
      this.pestana = "resultado";
    }
  }

  /** La misma comparativa que la consulta; «Ver resultado» abre aquí el Resultado completo. */
  irAComparativa(): void {
    this.pestana = "comparativa";
  }

  /* ============================== Comunes ============================== */

  /**
   * La app corre SIN zonas (Angular 21, bootstrapApplication sin
   * provideZoneChangeDetection): una respuesta HTTP o un Swal no repintan
   * solos. Todo callback asincrono que cambia el estado termina aqui, como
   * los detectChanges de Evaluaciones IA Solser.
   */
  private pinta(): void {
    this.cdr.markForCheck();
  }

  num(v: unknown): number | null {
    return num(v);
  }

  @HostListener("document:keydown.escape")
  alEscape(): void {
    if (this.hayCuadroSwal()) {
      return;
    }
    if (this.firma) {
      // Se pierde solo el trazo y el PIN: el dictamen capturado se queda.
      this.cerrarFirma();
    } else if (this.accionPin) {
      this.cerrarAccion(true);
    } else if (this.carga) {
      this.cerrarCarga(true);
    } else if (this.cargaFicha) {
      this.cerrarCargaFicha(true);
    } else if (this.edicion) {
      this.cerrarEdicion(true);
    }
  }

  /** Tab no sale del cuadro abierto: da la vuelta entre lo que se puede enfocar dentro. */
  /**
   * Un Swal con botones abierto (una confirmación). Los toasts no cuentan:
   * con un aviso de 6 s a la vista, Escape y los botones siguen sirviendo.
   */
  private hayCuadroSwal(): boolean {
    const p = Swal.getPopup();
    return Swal.isVisible() && !!p && !p.classList.contains("swal2-toast");
  }

  /** @param donde la caja del cuadro abierto; el de la firma es app-firma-frc. */
  atrapaFoco(evento: Event, donde = ".hom-modal .app-dialog"): void {
    const caja = this.host.nativeElement.querySelector<HTMLElement>(donde);
    if (!caja) {
      return;
    }
    const focables = Array.from(
      caja.querySelectorAll<HTMLElement>(
        "input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex='-1'])",
      ),
    ).filter((el) => el.offsetParent !== null);
    if (!focables.length) {
      evento.preventDefault();
      return;
    }
    const primero = focables[0];
    const ultimo = focables[focables.length - 1];
    const atras = (evento as KeyboardEvent).shiftKey;
    const activo = document.activeElement;
    if (atras && (activo === primero || !caja.contains(activo))) {
      evento.preventDefault();
      ultimo.focus();
    } else if (!atras && (activo === ultimo || !caja.contains(activo))) {
      evento.preventDefault();
      primero.focus();
    }
  }

  /**
   * @param alterno a dónde va el foco si lo que abrió el cuadro ya no
   *   existe (firmar quita el botón de Firmar, reabrir el de Reabrir).
   */
  private devuelveFoco(alterno?: string): void {
    this.cdr.detectChanges();
    const volverA = this.focoDesde;
    this.focoDesde = null;
    if (volverA && volverA.isConnected) {
      volverA.focus();
    } else if (alterno) {
      this.host.nativeElement.querySelector<HTMLElement>(alterno)?.focus();
    }
  }

  private mensaje(res: HomologacionRes | null | undefined, porOmision: string): string {
    // AUTH-001 viaja en un 200 con codigoError: ningún interceptor lo ve.
    if (res && res.codigoError === "AUTH-001") {
      return "Tu sesión caducó. Vuelve a entrar.";
    }
    return (res && res.mensajeError) || porOmision;
  }

  /** titleText y no title: el mensaje puede citar valores del archivo y Swal pinta title como HTML. */
  private aviso(texto: string, icono: "success" | "error" | "warning" | "info"): void {
    Swal.fire({
      toast: true,
      position: "top-end",
      icon: icono,
      titleText: texto,
      showConfirmButton: false,
      timer: icono === "error" ? 6000 : 2500,
      timerProgressBar: true,
    });
  }

  private subeAlInicio(): void {
    setTimeout(() => this.host.nativeElement.scrollIntoView({ block: "start" }));
  }
}
